import {
  GOOGLE_DRIVE_PLUGIN_ID,
  MediaImportedEvent,
  MediaImporterProps,
  OnAuthError,
  OnMediaRejected,
  YOUTUBE_URL_PLUGIN_ID,
  ZOOM_PLUGIN_ID,
} from '@sparemin/media-import';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UseConnectedModalReturnType } from 'containers/ConnectedModal/useConnectedModal';
import { onFileUpload } from 'redux/modules/mixpanel';
import { showError } from 'redux/modules/notification';
import {
  videoUploadLimitHoursSelector,
  videoUploadLimitMbSelector,
} from 'redux/modules/pricing';
import { Dispatch } from 'redux/types';
import { MediaUploadContext } from 'utils/constants';
import { megabytesToBytes } from 'utils/file';
import { getSupportedVideoMimeTypes } from 'utils/formats';
import { buildImportedFileUrl, fetchImportedFilePreviewUrl } from './utils';

export interface UseMediaUploadModalConfig
  extends Pick<UseConnectedModalReturnType, 'onHide'> {
  uploadContext: MediaUploadContext;
}

export interface UseMediaUploadModalResult {
  isLoadingFile: boolean;
  mediaImporterDefaults: Pick<
    MediaImporterProps,
    'accept' | 'maxSizeBytes' | 'maxDurationMillis'
  >;
  mediaImporterCallbacks: Pick<
    MediaImporterProps,
    'onMediaImported' | 'onMediaAccepted' | 'onMediaRejected' | 'onAuthError'
  >;
}

export default function useMediaUploadModal({
  uploadContext,
  onHide,
}: UseMediaUploadModalConfig) {
  const [isLoadingFile, setIsLoadingFile] = useState(false);

  const uploadLimitMb = useSelector(videoUploadLimitMbSelector);
  const uploadLimitHour = useSelector(videoUploadLimitHoursSelector);

  const dispatch = useDispatch<Dispatch>();

  const handleMediaImported = useCallback((): void => {
    setIsLoadingFile(true);
  }, []);

  const handleMediaAccepted = useCallback(
    async (args: MediaImportedEvent): Promise<void> => {
      const { payload, pluginId } = args;

      switch (pluginId) {
        case GOOGLE_DRIVE_PLUGIN_ID: {
          const {
            url,
            params: downloadInfoParams,
            headers,
          } = payload.file.downloadInfo;

          dispatch(
            onFileUpload({
              source: 'googleDrive',
              fileName: payload.file.name,
              dropZoneType: uploadContext,
            }),
          );

          const previewUrl = await fetchImportedFilePreviewUrl(
            url,
            downloadInfoParams,
            headers,
          );

          return onHide({
            title: payload?.file?.name,
            provider: 'googleDrive',
            providerUserId: payload.user.providerUserId,
            previewUrl,
            googleDriveFileId: payload.file.id,
          });
        }

        case ZOOM_PLUGIN_ID: {
          const {
            downloadInfo,
            id,
            meetingId,
            meetingTopic,
          } = payload.recording;
          const { url, params: downloadInfoParams } = downloadInfo;

          const previewUrl = buildImportedFileUrl(url, downloadInfoParams);

          dispatch(
            onFileUpload({
              source: 'zoom',
              fileName: meetingTopic,
              dropZoneType: uploadContext,
            }),
          );

          return onHide({
            title: meetingTopic,
            provider: 'zoom',
            providerUserId: payload.user.providerUserId,
            previewUrl,
            zoomMeetingId: meetingId,
            zoomRecordingFileId: id,
          });
        }

        case YOUTUBE_URL_PLUGIN_ID: {
          const {
            audioLanguageCode,
            autoGeneratedCaptionLanguageCode,
            url,
            previewVideoUrl,
            hasMostReplayedData,
            title,
            videoId,
          } = payload;

          dispatch(
            onFileUpload({
              source: 'youtube',
              url,
              dropZoneType: uploadContext,
            }),
          );

          return onHide({
            title,
            provider: 'youtube',
            previewUrl: previewVideoUrl,
            hasMostReplayedData,
            youtubeAudioLanguageCode: audioLanguageCode,
            youtubeAutoGeneratedCaptionLanguageCode: autoGeneratedCaptionLanguageCode,
            youtubeUrl: url,
            videoId,
          });
        }

        default: {
          return undefined;
        }
      }
    },
    [dispatch, uploadContext, onHide],
  );

  const handleMediaRejected = useCallback<OnMediaRejected>(
    (rejection): void => {
      setIsLoadingFile(false);

      const { reason } = rejection.payload;

      let message = '';

      switch (reason) {
        case 'WRONG_TYPE': {
          message =
            'Unfortunately, the uploaded file is not a supported file type';
          break;
        }

        case 'TOO_LARGE': {
          message = `Upload size exceeds ${uploadLimitMb / 1000}GB`;
          break;
        }

        case 'TOO_LONG': {
          message = `Upload length exceeds ${uploadLimitHour} hour max`;
          break;
        }

        default: {
          message = 'Something went wrong! Please try again.';
          break;
        }
      }

      dispatch(showError(message));
    },
    [dispatch, uploadLimitHour, uploadLimitMb],
  );

  const handleAuthError = useCallback<OnAuthError>((): void => {
    dispatch(showError('Error connecting to the platform. Please try again.'));
  }, [dispatch]);

  const mediaImporterDefaults = useMemo(
    (): Partial<MediaImporterProps> => ({
      accept: getSupportedVideoMimeTypes(),
      maxSizeBytes: megabytesToBytes(uploadLimitMb),
      maxDurationMillis: 60 * 60 * Number(uploadLimitHour) * 1000,
    }),
    [uploadLimitHour, uploadLimitMb],
  );

  const mediaImporterCallbacks = useMemo(
    (): Partial<MediaImporterProps> => ({
      onMediaImported: handleMediaImported,
      onMediaAccepted: handleMediaAccepted,
      onMediaRejected: handleMediaRejected,
      onAuthError: handleAuthError,
    }),
    [
      handleAuthError,
      handleMediaAccepted,
      handleMediaImported,
      handleMediaRejected,
    ],
  );

  return {
    isLoadingFile,
    mediaImporterDefaults,
    mediaImporterCallbacks,
  };
}
